import { NOT_FOUND } from 'redux-first-router';
import moment from 'moment';
import { get } from 'lodash-es';

import { actions as customerActions } from '../ducks/customer.js';
import { actions as propertyActions } from '../ducks/properties.js';
import { actions as authActions } from '../ducks/auth.js';
import { types as appTypes, actions as appActions } from '../ducks/app.js';
import constants from '../constants.js';
import { getString } from '../helpers/stringHelpers.js';
import {
  getCurrentOutage,
  inOutage,
  isFeatureDisabled,
} from '../helpers/outageHelpers';
import {
  countJobsRaisedToday,
  getWorkRequestAPITypeFromString,
} from '../helpers/workRequestHelpers';
import Cookies from 'universal-cookie';
import { hasRole } from '../helpers/authHelpers';
import analyticsApi from '../api/analytics.js';

// Containers
import Dash from '../containers/Dash/Dash.js';
import Auth from '../containers/Auth/Auth.js';
import Profile from '../containers/ProfileLanding/Profile.js';
import Accounts from '../containers/Accounts/Accounts.js';
import AccountDetails from '../containers/AccountDetails/AccountDetails.js';
import AccountTransactions from '../containers/AccountTransactions/AccountTransactions.js';
import ArrangementDetails from '../containers/ArrangementDetails/ArrangementDetails.js';
import WorkRequests from '../containers/WorkRequests/WorkRequests.js';
import WorkRequestsChild from '../containers/WorkRequestsChild/WorkRequestsChild.js';
import WorkRequestsItem from '../containers/WorkRequestsItem/WorkRequestsItem.js';
import WorkRequestsCreate from '../containers/WorkRequestsCreate/WorkRequestsCreate.js';
import Appointments from '../containers/Appointments/Appointments.js';
import WellbeingLanding from '../containers/WellbeingLanding/WellbeingLanding.js';
import WellbeingChild from '../containers/WellbeingChild/WellbeingChild.js';
import Letters from '../containers/Letters/Letters.js';
import ServiceLanding from '../containers/ServiceLanding/ServiceLanding.js';
import ServiceChild from '../containers/ServiceChild/ServiceChild.js';
import ApplicationLanding from '../containers/ApplicationLanding/ApplicationLanding.js';
import ApplicationChild from '../containers/ApplicationChild/ApplicationChild.js';
import ProfileChild from '../containers/ProfileChild/ProfileChild.js';
import ArrangementCreate from '../containers/ArrangementCreate/ArrangementCreate.js';
import Contact from '../containers/Contact/Contact.js';
import HelpCenter from '../containers/HelpCenter/HelpCenter.js';

export const types = {
  AUTH: 'ROUTING/AUTH',
  DASH: 'ROUTING/DASH',
  PROFILE: 'ROUTING/PROFILE',
  ACCOUNTS: 'ROUTING/ACCOUNTS',
  ACCOUNT_DETAILS: 'ROUTING/ACCOUNT_DETAILS',
  ACCOUNT_TRANSACTIONS: 'ROUTING/ACCOUNT_TRANSACTIONS',
  ARRANGEMENT_DETAILS: 'ROUTING/ARRANGEMENT_DETAILS',
  WORK_REQUESTS: 'ROUTING/WORK_REQUESTS',
  WORK_REQUESTS_CHILD: 'ROUTING/WORK_REQUESTS_CHILD',
  WORK_REQUESTS_ITEM: 'ROUTING/WORK_REQUESTS_ITEM',
  WORK_REQUESTS_CREATE: 'ROUTING/WORK_REQUESTS_CREATE',
  APPOINTMENTS: 'ROUTING/APPOINTMENTS',
  WELLBEING: 'ROUTING/WELLBEING',
  WELLBEING_LANDING: 'ROUTING/WELLBEING_LANDING',
  WELLBEING_CHILD: 'ROUTING/WELLBEING_CHILD',
  LETTERS: 'ROUTING/LETTERS',
  SERVICE_LANDING: 'ROUTING/SERVICE_LANDING',
  SERVICE_CHILD: 'ROUTING/SERVICE_CHILD',
  APPLICATION_LANDING: 'ROUTING/APPLICATION_LANDING',
  APPLICATION_CHILD: 'ROUTING/APPLICATION_CHILD',
  PROFILE_CHILD: 'ROUTING/PROFILE_CHILD',
  ARRANGEMENT_CREATE: 'ROUTING/ARRANGEMENT_CREATE',
  CONTACT: 'ROUTING/CONTACT',
  HELP_CENTER: 'ROUTING/HELP_CENTER',
};

const doIfNotOutage = (dispatch, callback) => {
  const currentOutage = getCurrentOutage(outages);

  if (!cmsAccess && currentOutage && inOutage(currentOutage)) {
    dispatch(appActions.setAppOutage(currentOutage));
  } else {
    callback();
  }
};

const dashThunk = (dispatch, getState) => {
  const {
    account,
    contactDetails,
    customerData,
    preferences,
    letters,
    bankDetails,
  } = getState().customer;
  const {
    auth: { roles },
  } = getState();

  const callback = () => {
    dispatch(appActions.setAccountType(account));

    // If dash data has not been loaded yet, load it
    if (
      account.loaded == constants.UNLOADED &&
      hasRole(roles, constants.ROLE_ACCOUNT_READ)
    ) {
      dispatch(customerActions.getAccount());
    }
    // Contact details are required for surveys that appear on Dash.
    if (
      contactDetails.loaded == constants.UNLOADED &&
      hasRole(roles, constants.ROLE_CUSTOMER_READ)
    ) {
      dispatch(customerActions.getContactDetails());
    }

    // If preferences data has not been loaded yet, load it
    if (
      preferences.loaded == constants.UNLOADED &&
      hasRole(roles, constants.ROLE_CUSTOMER_READ)
    ) {
      dispatch(customerActions.getPreferences());

      // Use this opportunity to save last used value
      saveLastUsed(dispatch);
    }

    // If letters data has not been loaded yet, load it
    if (letters.loaded == constants.UNLOADED) {
      dispatch(customerActions.getLetters());
    }

    // If letters data has not been loaded yet, load it
    if (bankDetails.loaded == constants.UNLOADED) {
      dispatch(customerActions.getBankDetails());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const profileThunk = (dispatch, getState) => {
  const { contactDetails, tenancy, bankDetails, preferences, letters } =
    getState().customer;
  const {
    auth: { roles },
  } = getState();

  const callback = () => {
    // If dash data has not been loaded yet, load it
    if (contactDetails.loaded == constants.UNLOADED) {
      dispatch(customerActions.getContactDetails());
    }
    if (tenancy.loaded == constants.UNLOADED) {
      dispatch(customerActions.getAccount());
    }
    if (
      bankDetails.loaded == constants.UNLOADED &&
      hasRole(roles, constants.ROLE_BANK_READ)
    ) {
      dispatch(customerActions.getBankDetails());
    }
    // If letters data has not been loaded yet, load it
    if (letters.loaded == constants.UNLOADED) {
      dispatch(customerActions.getLetters());
    }

    // If preferences data has not been loaded yet, load it
    if (preferences.loaded == constants.UNLOADED) {
      dispatch(customerActions.getPreferences());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const accountThunk = (dispatch, getState) => {
  const { account, preferences, letters } = getState().customer;

  const callback = () => {
    // If dash data has not been loaded yet, load it
    if (account.loaded == constants.UNLOADED) {
      dispatch(customerActions.getAccount());
    }

    // If letters data has not been loaded yet, load it
    if (letters.loaded == constants.UNLOADED) {
      dispatch(customerActions.getLetters());
    }

    // If preferences data has not been loaded yet, load it
    if (preferences.loaded == constants.UNLOADED) {
      dispatch(customerActions.getPreferences());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const accountDetailsThunk = (dispatch, getState, action) => {
  const { transactions, preferences, letters } = getState().customer;
  let { account } = action.action.payload;

  // Make sure account is always set sensibly
  if (['rent', 'maintenance'].indexOf(account) === -1) {
    dispatch(actions.goAccounts());
  } else {
    const callback = () => {
      dispatch(appActions.setAccountType(account));

      if (transactions[account].loaded == constants.UNLOADED) {
        dispatch(customerActions.getTransactions());
      }

      // If letters data has not been loaded yet, load it
      if (letters.loaded == constants.UNLOADED) {
        dispatch(customerActions.getLetters());
      }

      // If preferences data has not been loaded yet, load it
      if (preferences.loaded == constants.UNLOADED) {
        dispatch(customerActions.getPreferences());
      }
    };

    doIfNotOutage(dispatch, callback);
  }
};

const lettersThunk = (dispatch, getState) => {
  const { letters, preferences } = getState().customer;

  const callback = () => {
    // If dash data has not been loaded yet, load it
    if (letters.loaded == constants.UNLOADED) {
      dispatch(customerActions.getLetters());
    }

    // If preferences data has not been loaded yet, load it
    if (preferences.loaded == constants.UNLOADED) {
      dispatch(customerActions.getPreferences());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const workRequestsThunk = (dispatch, getState) => {
  const { workRequests, contactDetails, preferences, letters } =
    getState().customer;

  const callback = () => {
    // If dash data has not been loaded yet, load it
    if (workRequests.open.loaded == constants.UNLOADED) {
      dispatch(customerActions.getWorkRequests('open'));
    }
    if (contactDetails.loaded == constants.UNLOADED) {
      dispatch(customerActions.getContactDetails());
    }
    // If letters data has not been loaded yet, load it
    if (letters.loaded == constants.UNLOADED) {
      dispatch(customerActions.getLetters());
    }

    // If preferences data has not been loaded yet, load it
    if (preferences.loaded == constants.UNLOADED) {
      dispatch(customerActions.getPreferences());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const workRequestsChildThunk = (dispatch, getState, action) => {
  const { workRequests, contactDetails } = getState().customer;
  let { type } = action.action.payload;

  if (['current', 'previous', 'cancelled'].indexOf(type) === -1) {
    dispatch(actions.goWorkRequests());
  } else {
    const callback = () => {
      const apiType = getWorkRequestAPITypeFromString(type);
      // If dash data has not been loaded yet, load it
      if (workRequests[apiType].loaded == constants.UNLOADED) {
        dispatch(customerActions.getWorkRequests(apiType));
      }
      if (contactDetails.loaded == constants.UNLOADED) {
        dispatch(customerActions.getContactDetails());
      }
    };

    doIfNotOutage(dispatch, callback);
  }
};

const workRequestsItemThunk = (dispatch, getState, action) => {
  const { workRequests, contactDetails } = getState().customer;
  let { type } = action.action.payload;

  if (true == false) {
    dispatch(actions.goWorkRequests());
  } else {
    const callback = () => {
      const apiType = getWorkRequestAPITypeFromString(type);
      // If dash data has not been loaded yet, load it
      if (workRequests[apiType].loaded == constants.UNLOADED) {
        dispatch(customerActions.getWorkRequests(apiType));
      }
      if (contactDetails.loaded == constants.UNLOADED) {
        dispatch(customerActions.getContactDetails());
      }
    };

    doIfNotOutage(dispatch, callback);
  }
};

const workRequestsCreateThunk = (dispatch, getState) => {
  const { workRequests } = getState().customer;
  const { spaces } = getState().properties;

  const callback = () => {
    // If dash data has not been loaded yet, load it
    if (workRequests.open.loaded == constants.UNLOADED) {
      dispatch(customerActions.getWorkRequests('open'));
    }
    if (spaces.loaded == constants.UNLOADED) {
      dispatch(propertyActions.getSpaces());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const appointmentsThunk = (dispatch, getState) => {
  const { account, preferences, letters } = getState().customer;

  const callback = () => {
    // If dash data has not been loaded yet, load it
    if (account.loaded == constants.UNLOADED) {
      dispatch(customerActions.getAccount());
    }
    // If letters data has not been loaded yet, load it
    if (letters.loaded == constants.UNLOADED) {
      dispatch(customerActions.getLetters());
    }

    // If preferences data has not been loaded yet, load it
    if (preferences.loaded == constants.UNLOADED) {
      dispatch(customerActions.getPreferences());
    }
  };

  doIfNotOutage(dispatch, callback);
};

const saveLastUsed = (dispatch) => {
  // Remove milliseconds from datetime string
  const currentDateTime = moment().toISOString(true).split('.')[0];

  dispatch(
    customerActions.createPreferences({
      preferences: [
        {
          key: 'last-used-date',
          value: currentDateTime,
        },
      ],
    })
  );
};

export const routesMap = {
  [types.AUTH]: {
    path: '/auth',
    page: Auth,
    auth: false,
  },
  [types.DASH]: {
    path: '/dash',
    page: Dash,
    auth: true,
    thunk: dashThunk,
  },
  [types.PROFILE]: {
    path: '/profile',
    page: Profile,
    auth: true,
    role: constants.ROLE_CUSTOMER_READ,
    editRole: constants.ROLE_CUSTOMER_WRITE,
    editBankRole: constants.ROLE_BANK_WRITE,
    thunk: profileThunk,
    confirmLeave: (state, action) => {
      if (state.app.unsaved) {
        return getString('blockModal', 'MESSAGE');
      }
    },
  },
  [types.ACCOUNTS]: {
    path: '/accounts',
    page: Accounts,
    auth: true,
    thunk: accountThunk,
    role: constants.ROLE_ACCOUNT_READ,
  },
  [types.ACCOUNT_DETAILS]: {
    path: '/accounts/:account',
    page: AccountDetails,
    auth: true,
    thunk: accountDetailsThunk,
    role: constants.ROLE_ACCOUNT_READ,
  },
  [types.ACCOUNT_TRANSACTIONS]: {
    path: '/accounts/:account/transactions',
    page: AccountTransactions,
    auth: true,
    thunk: accountDetailsThunk,
    role: constants.ROLE_ACCOUNT_READ,
  },
  [types.ARRANGEMENT_DETAILS]: {
    path: '/accounts/:account/arrangements',
    page: ArrangementDetails,
    auth: true,
    thunk: accountDetailsThunk,
  },
  [types.ARRANGEMENT_CREATE]: {
    path: '/create-arrangement/:account',
    page: ArrangementCreate,
    auth: true,
    thunk: accountDetailsThunk,
    role: constants.ROLE_ARRANGEMENT_WRITE,
    confirmLeave: (state, action) => {
      if (state.app.unsaved) {
        return getString('blockModal', 'MESSAGE');
      }
    },
  },
  [types.WORK_REQUESTS]: {
    path: '/maintenance',
    page: WorkRequests,
    auth: true,
    thunk: workRequestsThunk,
    role: constants.ROLE_REQUEST_READ,
  },
  [types.WORK_REQUESTS_CREATE]: {
    path: '/maintenance/create',
    page: WorkRequestsCreate,
    auth: true,
    thunk: workRequestsCreateThunk,
    role: constants.ROLE_REQUEST_WRITE,
    confirmLeave: (state, action) => {
      if (state.app.unsaved) {
        return getString('blockModal', 'MESSAGE');
      }
    },
  },
  [types.WORK_REQUESTS_CHILD]: {
    path: '/maintenance/:type',
    page: WorkRequestsChild,
    auth: true,
    thunk: workRequestsChildThunk,
    role: constants.ROLE_REQUEST_READ,
  },
  [types.WORK_REQUESTS_ITEM]: {
    path: '/maintenance/:type/:item',
    page: WorkRequestsItem,
    auth: true,
    thunk: workRequestsItemThunk,
    role: constants.ROLE_REQUEST_READ,
  },
  [types.APPOINTMENTS]: {
    path: '/appointments',
    page: Appointments,
    auth: true,
    role: constants.ROLE_APPOINTMENTS_READ,
    thunk: appointmentsThunk,
  },
  [types.WELLBEING_LANDING]: {
    path: '/wellbeing',
    page: WellbeingLanding,
    auth: true,
    thunk: appointmentsThunk,
  },
  [types.WELLBEING_CHILD]: {
    path: '/wellbeing/:page',
    page: WellbeingChild,
    auth: true,
    thunk: appointmentsThunk,
  },
  [types.LETTERS]: {
    path: '/letters',
    page: Letters,
    auth: true,
    thunk: lettersThunk,
  },
  [types.SERVICE_LANDING]: {
    path: '/government',
    page: ServiceLanding,
    auth: true,
    thunk: appointmentsThunk,
  },
  [types.SERVICE_CHILD]: {
    path: '/government/:page',
    page: ServiceChild,
    auth: true,
    thunk: appointmentsThunk,
  },
  [types.APPLICATION_LANDING]: {
    path: '/applications',
    page: ApplicationLanding,
    auth: true,
    thunk: profileThunk,
  },
  [types.APPLICATION_CHILD]: {
    path: '/applications/:page',
    page: ApplicationChild,
    auth: true,
    thunk: profileThunk,
  },
  [types.PROFILE_CHILD]: {
    path: '/profile/:page',
    page: ProfileChild,
    auth: true,
    thunk: profileThunk,
  },
  [types.CONTACT]: {
    path: '/contact',
    page: Contact,
    auth: true,
    thunk: profileThunk,
  },
  [types.HELP_CENTER]: {
    path: '/help-center',
    page: HelpCenter,
    auth: true,
    thunk: appointmentsThunk,
  },
};

export const initialState = {
  page: null,
};

export default (state = initialState, action) => {
  const { type, payload } = action;
  // Try to get route information for an action type
  const route = routesMap[type];

  if (route) {
    return {
      ...state,
      page: route.page,
    };
  }

  return state;
};

// Options for redux-first-router
export const options = {
  onBeforeChange: (dispatch, getState, action) => {
    const { account } = action.action.payload;

    if (account == 'rent' || account == 'maintenance') {
      dispatch(appActions.setAccountType(account));
    }
    dispatch(appActions.setRouteChangeTime(Date.now()));
  },
  onAfterChange: (dispatch, getState, action) => {
    const {
      location: { type, prev },
      auth: { authenticated, error, jwtExpiry, roles },
      customer: { workRequests, tenancy, newArrangementState },
      properties: { createdWorkRequests },
      app: { numberBankUpdates },
    } = getState();
    const { account } = action.action.payload;
    const cookies = new Cookies();

    // Ensure user is authenticated.
    if (!authenticated && type !== types.AUTH) {
      dispatch(actions.goAuth());
      return;
    }

    if (type == NOT_FOUND) {
      dispatch(actions.goDash());
      return;
    }

    // Currently only maintenance/repairs and rent account is supported.
    if (type == types.ACCOUNT_DETAILS) {
      if (account != 'rent' && account != 'maintenance') {
        dispatch(actions.goDash());
      }
    }

    // Refresh account details after leavng the create arrangement page
    // if an arrangement was created.
    if (
      prev.type == types.ARRANGEMENT_CREATE &&
      newArrangementState == constants.STATE_SUCCESS
    ) {
      dispatch(customerActions.getAccount());
    }

    // Reset the scrollToContacts state when leaving the profile page.
    if (type != types.PROFILE) {
      dispatch(appActions.toggleScrollToContacts(false));
    }

    // Take user back to profile page if trying to go to notifications page
    // and notifications is disabled.
    if (
      type === types.PROFILE_CHILD &&
      isFeatureDisabled('NOTIFICATIONS', tenancy.data.area) &&
      action.action.payload.page == 'Notifications'
    ) {
      dispatch(actions.goProfile());
    }

    // Reset the selected appoint state when leaving the appointment page.
    if (type != types.APPOINTMENTS) {
      dispatch(appActions.setSelectedAppointment(null));
    }

    // Take user away from work request create if they have reached the daily limit.
    // Or if maintenance reqquest has been disabled in the CMS
    if (type == types.WORK_REQUESTS_CREATE) {
      const createdJobsCount = createdWorkRequests.jobs.length;
      const openWorkRequestsData = get(workRequests, 'open.data', []);
      const jobsRaisedTodayCount = countJobsRaisedToday(openWorkRequestsData);
      if (
        jobsRaisedTodayCount + createdJobsCount >=
          constants.DAILY_REQUEST_LIMIT ||
        isFeatureDisabled('MAINTENANCE_WRITE', tenancy.data.area)
      ) {
        dispatch(actions.goDash());
      }
    }

    const route = routesMap[type];
    const needsAuth = route.auth;
    const role = route.role;
    const editRole = route.editRole;
    const editBankRole = route.editBankRole;
    const routeAction = action.action.payload.action;

    if (
      (!needsAuth && (authenticated || error)) ||
      (role && !hasRole(roles, role)) ||
      (routeAction === 'edit' && !hasRole(roles, editRole)) ||
      (routeAction === 'edit-bank' && !hasRole(roles, editBankRole)) ||
      (routeAction === 'edit-bank' &&
        numberBankUpdates >= constants.BANK_UPDATE_LIMIT)
    ) {
      dispatch(actions.goDash());
      return;
    }

    if (needsAuth && !authenticated && !error) {
      dispatch(actions.goAuth());
      return;
    }

    const now = moment().unix();
    let {
      auth: { lastActive },
    } = getState();
    const timeoutMins = 10;
    if (!lastActive) {
      lastActive = now;
    }
    const endDate = moment.unix(lastActive).add(timeoutMins, 'minutes');
    if (
      authenticated &&
      (now >= jwtExpiry || !moment().isBetween(lastActive, endDate))
    ) {
      dispatch(authActions.signout());
    }
    // On route change we are no longer in an unsaved data state.
    dispatch(appActions.setUnsaved(false));

    // Send additional GA event when user switches to current maintenance.
    const prevPath = action.action.meta.location.prev.pathname;
    const currentPath = action.action.meta.location.current.pathname;
    if (prevPath !== currentPath) {
      if (type === types.WORK_REQUESTS) {
        analyticsApi.event('Maintenance', 'Click', 'Current Maintenance');
      }
      if (type == types.ARRANGEMENT_CREATE) {
        if (account == 'rent') {
          analyticsApi.event(
            'Rent Arrangement',
            'Create',
            'Start arrangement creation'
          );
        } else if (account == 'maintenance') {
          analyticsApi.event(
            'Maintenance Arrangement',
            'Create',
            'Start arrangement creation'
          );
        }
      }
    }
  },
  initialDispatch: false,
};

export const actions = {
  goAuth: () => ({ type: types.AUTH }),
  goDash: () => ({ type: types.DASH }),
  goProfile: () => ({ type: types.PROFILE, payload: { action: 'view' } }),
  goProfileEdit: () => ({
    type: types.PROFILE_CHILD,
    payload: { page: 'Contact-Details' },
  }),
  goProfileEditBank: (id) => ({
    type: types.PROFILE,
    payload: {
      action: 'edit-bank',
      id: id,
    },
  }),
  goAccounts: () => ({ type: types.ACCOUNTS }),
  goAccountDetails: (account) => ({
    type: types.ACCOUNT_DETAILS,
    payload: {
      account: account,
    },
  }),
  goAccountTransactions: (account) => ({
    type: types.ACCOUNT_TRANSACTIONS,
    payload: {
      account: account,
    },
  }),
  goWorkRequests: () => ({ type: types.WORK_REQUESTS }),
  goWorkRequestsChild: (type) => ({
    type: types.WORK_REQUESTS_CHILD,
    payload: {
      type: type,
    },
  }),
  goWorkRequestsItem: (type, item) => ({
    type: types.WORK_REQUESTS_ITEM,
    payload: {
      type: type,
      item: item,
    },
  }),
  goWorkRequestsCreate: () => ({ type: types.WORK_REQUESTS_CREATE }),
  goArrangementDetails: (account) => ({
    type: types.ARRANGEMENT_DETAILS,
    payload: {
      account: account,
    },
  }),
  goArrangementCreate: (account) => ({
    type: types.ARRANGEMENT_CREATE,
    payload: {
      account: account,
    },
  }),
  goAppointments: () => ({ type: types.APPOINTMENTS }),
  goWellbeing: () => ({ type: types.WELLBEING }),
  goWellbeingLanding: () => ({ type: types.WELLBEING_LANDING }),
  goWellbeingChild: (page) => ({
    type: types.WELLBEING_CHILD,
    payload: {
      page: page,
    },
  }),
  goLetters: () => ({ type: types.LETTERS }),
  goServiceLanding: () => ({ type: types.SERVICE_LANDING }),
  goServiceChild: (page) => ({
    type: types.SERVICE_CHILD,
    payload: {
      page: page,
    },
  }),
  goApplicationLanding: () => ({ type: types.APPLICATION_LANDING }),
  goApplicationChild: (page) => ({
    type: types.APPLICATION_CHILD,
    payload: {
      page: page,
    },
  }),
  goProfileChild: (page) => ({
    type: types.PROFILE_CHILD,
    payload: {
      page: page,
    },
  }),
  goContact: () => ({ type: types.CONTACT }),
  goHelpCenter: () => ({ type: types.HELP_CENTER }),
};
